import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { FilterBarService } from '@modules/filters/filter-bar.service';
import { ActionButtonsComponent } from '@modules/shared/action-buttons/action-buttons.component';
import { FlattenedLabel } from '@pixacare/dtos';
import { match } from '@pixacare/shared';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { ActionButton } from '@shared/models/menu-actions/action-buttons';
import { menuActionsToActionsButtons } from '@shared/models/menu-actions/adapters/menu-action.adapters';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { UserNamePipe } from '@shared/pipes/user-name.pipe';
import { TuiComparator, TuiTable } from '@taiga-ui/addon-table';
import { TuiFade } from '@taiga-ui/kit';
import { TuiCardLarge } from '@taiga-ui/layout';
import { map, Observable } from 'rxjs';
import { LabelComponent } from '../label/label.component';

@Component({
  selector: 'pxc-label-table',
  imports: [
    CommonModule,
    ScrollingModule,
    TuiTable,
    LabelComponent,
    EmptyStateComponent,
    ActionButtonsComponent,
    TranslocoDirective,
    TuiCardLarge,
    TuiFade,
    UserNamePipe,
  ],
  templateUrl: './label-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class LabelTableComponent implements OnInit, OnChanges {
  @Input() labels: FlattenedLabel[];
  @Input() actions: MenuAction[];
  @Input() displayStats = false;

  search$: Observable<string>;
  labels$: Observable<FlattenedLabel[]>;

  columns: string[];
  actionButtons: ActionButton[];

  private readonly filterBarService = inject(FilterBarService);

  constructor() {
    this.search$ = this.filterBarService.search$;
  }

  ngOnInit(): void {
    this.filterBarService.init();

    this.actionButtons = menuActionsToActionsButtons(this.actions);

    this.columns = [
      'labelInstance.word',
      ...(this.displayStats ? ['data.subscribersCount', 'userLabelStatsInstance.count'] : []),
      'createdBy',
      'actions',
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.labels) {
      this.labels$ = this.search$.pipe(
        map((search) => this.labels.filter((fLabel) => match(search, [fLabel.labelInstance.word]))),
      );
    }
  }

  labelSorter: TuiComparator<FlattenedLabel> = (a, b) => a.labelInstance?.word.localeCompare(b.labelInstance?.word);

  usersSorter: TuiComparator<FlattenedLabel> = (a, b) =>
    (a.data?.subscribersCount ?? -1) - (b.data?.subscribersCount ?? -1);

  usageSorter: TuiComparator<FlattenedLabel> = (a, b) =>
    (a.userLabelStatsInstance?.count ?? -1) - (b.userLabelStatsInstance?.count ?? -1);

  createdBySorter: TuiComparator<FlattenedLabel> = (a, b) =>
    (a.createdBy?.firstName ?? 'zz').localeCompare(b.createdBy?.firstName ?? 'zz');

  trackByLabelId(index: number, label: FlattenedLabel): number {
    return label.labelInstance.id;
  }
}
